import React from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { AlgoliaSearchProvider } from "components/algolia";
import { CatalogCTA } from "components/catalog-cta";
import { IntegrationsHero } from "components/integrations-hero";
import IntegrationsCatalog from "components/integrations/integrations-catalog";
import { Page } from "components/page";
import { getAnnouncementBanner, getPage, type Banner, type Page as SanityPage } from "lib/sanity";
import { type GetStaticProps, type NextPage } from "next";
import { renderToString } from "react-dom/server";
import { getServerState, type InstantSearchServerState } from "react-instantsearch";
import { theme } from "theme/theme";
import { getPageMetaImage } from "utils";
import heroImage from "../../../public/assets/integrations/hero.svg";

type PageProps = {
  page: SanityPage;
  serverState?: InstantSearchServerState;
  announcementBanner?: Banner;
};

const Integrations: NextPage<Readonly<PageProps>> = ({ page, serverState, announcementBanner }) => {
  return (
    <AlgoliaSearchProvider
      serverState={serverState}
      appId={process.env.NEXT_PUBLIC_ALGOLIA_INTEGRATIONS_APP_ID ?? ""}
      searchApiKey={process.env.NEXT_PUBLIC_ALGOLIA_INTEGRATIONS_SEARCH_API_KEY ?? ""}
      filters="NOT status:deprecated"
    >
      <Page
        title={page.metaTitle}
        description={page.metaDescription}
        image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
        announcementBanner={announcementBanner}
        footerOptions={{ hideCta: true }}
      >
        <IntegrationsHero title={page.title} image={heroImage} />

        <IntegrationsCatalog />

        <CatalogCTA
          chatButtonText="Request an integration"
          chatButtonDescription="We're shipping new integrations every week. Just let us know what you need, and we'll work with you to build it."
          chatPrompt="Hi, I would like to request a new integration: _____"
          linkText="Build your own integration"
          linkDescription="Need to sync data to internal APIs or niche, industry-specific software? Build your own custom destination in minutes—often without writing a single line of code."
          linkHref="/blog/announcing-the-custom-destination-toolkit-build-your-own-destination-in-minutes"
        />
      </Page>
    </AlgoliaSearchProvider>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([getPage("/integrations"), getAnnouncementBanner()]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const serverState = await getServerState(
    <ChakraProvider theme={theme}>
      <Integrations page={page} />
    </ChakraProvider>,
    { renderToString },
  );

  return {
    props: {
      page,
      serverState,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default Integrations;
